import './Search.sass';

import React, { FC, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRoute, useRouter } from 'react-router5';

import { SearchProps } from '$core/Search/Search.interface';
import {
  Header,
  Placeholder,
  PullToRefresh,
  ScrollView,
  SearchInput,
  Tabs,
  TabsItem,
} from '$uikit';
import { searchActions, searchSelector } from '$store/search';
import { Loader, MetaTags, UserCell } from '$shared/components';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { isBackNavigation } from '$navigation/router';
import { SEARCH } from '$shared/constants/pages';
import { SearchUserRow } from '$core/Search/SearchUserRow/SearchUserRow';
import { mainSelector } from '$store/main';
import { ContentPriorityType } from '$store/main/interface';
import { authIsLoggedSelector } from '$store/auth';

export const Search: FC<SearchProps> = () => {
  const dispatch = useDispatch();
  const { query, result, isLoading, nextFrom, isLoadingMore } =
    useSelector(searchSelector);
  const activeTab = 'peoples';
  const t = useTranslator();
  const { route } = useRoute();
  const sort = route.params.sort || 'popular';
  const isBack = isBackNavigation();
  const router = useRouter();
  const { contentPriority } = useSelector(mainSelector);
  const isLogged = useSelector(authIsLoggedSelector);
  const user = useCurrentUserInfo();

  useEffect(() => {
    if (user?.isolated) {
      location.replace('/');
    }
  }, []);

  useEffect(() => {
    if (!isBack) {
      dispatch(
        searchActions.search({
          query,
          isFast: true,
          tab: activeTab,
          sort: sort,
          city: 0,
        }),
      );
    }
  }, [sort, activeTab]);

  const handleSearchChange = useCallback(
    (query) => {
      dispatch(
        searchActions.search({
          query,
          isFast: query.length === 0,
          tab: activeTab,
          sort: sort,
          city: 0,
        }),
      );
    },
    [dispatch, activeTab, sort],
  );

  const handleSortChange = useCallback(
    (newSort) => {
      const params: any = {};
      if (newSort !== 'popular') {
        params.sort = newSort;
      } else {
        delete params.sort;
      }

      let newQuery = query;
      if (newSort === 'suggestions' && query.length > 0) {
        newQuery = '';
      }

      router.navigate(SEARCH, params, { replace: true });
      dispatch(
        searchActions.search({
          query: newQuery,
          isFast: query.length === 0,
          tab: activeTab,
          sort: newSort,
          city: 0,
        }),
      );
    },
    [router, query],
  );

  function renderContent() {
    if (!result.length) {
      if (isLoading) {
        return <Loader isWrapped />;
      }

      return <Placeholder centered>{t('search_empty')}</Placeholder>;
    }

    if (contentPriority === ContentPriorityType.ADULT || !isLogged) {
      return (
        <div className="Search__result">
          {result.map((item) => (
            <SearchUserRow key={item.objectId} item={item} />
          ))}
        </div>
      );
    }

    return result.map((item) => (
      <UserCell
        key={`${item.type}_${item.objectId}`}
        objectId={item.objectId}
        isFollowed={item.isFollowed}
        isGroup={item.type === 'group'}
      />
    ));
  }

  function renderPlaceholder() {
    if (activeTab === 'peoples') {
      return t('search_peoples_placeholder');
    } else if (activeTab === 'groups') {
      return t('search_groups_placeholder');
    } else {
      return t('search_all_placeholder');
    }
  }

  return (
    <>
      <MetaTags
        title={t('search_meta_title')}
        description={t('search_meta_description')}
      />
      <ScrollView
        className="Search"
        onLoadMore={() =>
          dispatch(
            searchActions.loadMore({
              tab: activeTab,
              sort: sort,
              city: 0,
            }),
          )
        }
        canLoadMore={!isLoading && !!nextFrom && !isLoadingMore}
        isLoadingMore={isLoadingMore}
      >
        <Header isSearch>
          <SearchInput
            placeholder={renderPlaceholder()}
            query={query}
            onChange={handleSearchChange}
          />
        </Header>
        <div className="Search__filters">
          <Tabs selected={sort} onChange={handleSortChange}>
            <TabsItem value="popular">{t('search_sort_popular')}</TabsItem>
            <TabsItem value="online">{t('search_sort_online')}</TabsItem>
            {/*<TabsItem value="suggestions">{t('search_sort_suggestions')}</TabsItem>*/}
            <TabsItem value="new">{t('search_sort_new')}</TabsItem>
          </Tabs>
        </div>
        <PullToRefresh isRefreshing={isLoading} onRefresh={() => handleSearchChange('')}>
          {renderContent()}
        </PullToRefresh>
      </ScrollView>
    </>
  );
};
